import { useTranslations } from "@zapier/i18n";

import {
  Alert,
  AlertDescription,
  Button,
  Key,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
} from "@zapier/design-system";

import {
  useAppConnectionSharingConfig,
  Connection,
  useAuthenticationSharing,
} from "@zapier/client";
import { useNotification } from "@zapier/utils";
import { SharingTypeahead } from "@zapier/shared";
import styles from "./ConnectionShareModal.module.css";
import { ConnectionShareModalList } from "./ConnectionShareModalList";

import {
  usePendingConnectionSharesState,
  useFormatConnectionShares,
  getFailedShareName,
} from "./utils";
import type { ConnectionShare } from "./utils";
import { ConnectionShareType } from "../types";
import { useManagedConnectionBlocks } from "../useManagedConnectionBlocks";
import type { CreateSharesOutput, DeleteSharesOutput } from "@zapier/bff";
interface Props {
  connection: Connection;
  id?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}
interface ContentProps extends Props {
  onSubmit: (
    membersToAdd: ConnectionShare[],
    membersToDelete: ConnectionShare[],
  ) => void;
  disableShare: boolean;
  /** Managed app: existing access can be removed, but nobody new can be added. */
  isRemoveOnly?: boolean;
}

function ConnectionShareContent({
  connection,
  onSubmit,
  setIsOpen,
  disableShare,
  isRemoveOnly = false,
}: ContentProps) {
  const t = useTranslations("connection-share-modal");

  const isSharedWithEveryone = connection?.sharedWithAll;
  const { shareWithEveryoneEnabled, isLoading: isConfigLoading } =
    useAppConnectionSharingConfig();

  const { formattedConnectionShares } = useFormatConnectionShares({
    connection,
  });

  // Keep a list of the shares that are to be added/removed since setConnection only happens when the modal is opened
  const { onAdd, onRemove, toAdd, toDelete, shares, isSharesLoading } =
    usePendingConnectionSharesState(formattedConnectionShares);

  const membersToAdd: ConnectionShare[] = toAdd.map((share) => ({
    ...share,
    shareType: share.shareType as ConnectionShareType,
  }));

  const membersToDelete: ConnectionShare[] = toDelete.map((share) => ({
    ...share,
    shareType: share.shareType as ConnectionShareType,
  }));

  const hasPendingRemovals = membersToDelete.length > 0;

  const selectShare = (id: Key | null) => {
    if (!id || id === "null" || disableShare || isRemoveOnly) return;
    if (String(id).includes("customuser")) {
      //   // remove "customuser_" prefix
      onAdd(String(id).replace(/^customuser_/, ""), ConnectionShareType.Member);
    }
    // if everyone/account/all is selected
    else if (String(id).includes("account")) {
      onAdd("all", ConnectionShareType.All);
    } else {
      // remove "group_" or "team_" prefix
      onAdd(
        String(id).replace(/^(group_|team_)/, ""),
        ConnectionShareType.Group,
      );
    }
  };

  const removeShare = ({
    id,
    shareType,
  }: {
    id: Key;
    shareType: ConnectionShareType;
  }) => {
    onRemove(String(id), shareType);
  };

  const submit = () => {
    onSubmit(membersToAdd, membersToDelete);
    setIsOpen(false);
  };

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={() => setIsOpen(false)}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            return event.key === "Enter" && setIsOpen(false);
          }
        }
      >
        <div className={styles.modalContent}>
          <ModalDialogHeader textAlign="start">
            {t("header.share-connection", {
              connectionTitle: connection?.title ?? "",
            })}
          </ModalDialogHeader>

          <ModalDialogBody textAlign="start">
            <div className={styles.typeahead}>
              {isRemoveOnly && (
                <Alert status="info">
                  <AlertDescription>
                    {t("warnings.remove-only")}
                  </AlertDescription>
                </Alert>
              )}
              {/* Don't allow user to add or remove shares if the connection permissions does not permit user to share */}
              {!disableShare && !isRemoveOnly && (
                <SharingTypeahead
                  isBusy={isConfigLoading}
                  isDisabled={isConfigLoading}
                  disableEveryoneOption={!shareWithEveryoneEnabled}
                  showEveryoneOption={!isSharedWithEveryone}
                  onSelect={selectShare}
                />
              )}
            </div>

            <div>
              <ConnectionShareModalList
                disableShare={disableShare}
                isLoading={isSharesLoading}
                onRemove={removeShare}
                shares={shares}
              />
            </div>

            <div
              className={styles.pendingRemovalWarning}
              data-testid="pending-removal-warning"
              data-visible={hasPendingRemovals}
              aria-hidden={!hasPendingRemovals}
              role="status"
            >
              <div>
                <Alert status="warning">
                  <AlertDescription>
                    {t("warnings.pending-removal")}
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </ModalDialogBody>

          <ModalDialogFooter>
            <Button
              variant="secondary"
              onPress={() => setIsOpen(false)}
              type="button"
            >
              {t("buttons.cancel")}
            </Button>
            <Button isDisabled={disableShare} onPress={submit} type="submit">
              {t("buttons.submit", { isRemoveOnly: String(isRemoveOnly) })}
            </Button>
          </ModalDialogFooter>
        </div>
      </form>
    </ModalDialogRoot>
  );
}

export default function ConnectionShare({
  id,
  isOpen,
  setIsOpen,
  connection,
}: Props) {
  const { createNotification } = useNotification();
  const { createAuthShares, deleteAuthShares } = useAuthenticationSharing();
  const t = useTranslations("connection-share-modal");
  const { isShareBlocked } = useManagedConnectionBlocks(connection);
  // This callback needs to live here so that it is not canceled when the modal closes
  const handleSubmit = async (
    membersToAdd: ConnectionShare[],
    membersToDelete: ConnectionShare[],
  ) => {
    if (!connection?.permissions?.share || !id) return;

    const formatShares = (shares: ConnectionShare[]) =>
      shares.map(({ id, shareType }) => ({ id: String(id), shareType }));

    // createShares/deleteShares resolve with counts, not rejections; add/delete are caught independently so one's transport failure doesn't discard the other's results.
    let successful = 0;
    let failed = 0;
    const failedShareNames: string[] = [];

    if (membersToAdd.length > 0) {
      try {
        const result: CreateSharesOutput = await new Promise(
          (resolve, reject) => {
            createAuthShares(
              { connectionId: id, shares: formatShares(membersToAdd) },
              { onSuccess: resolve, onError: reject },
            );
          },
        );
        successful += result.successful;
        failed += result.failed;
        failedShareNames.push(
          ...result.failedShares.map((share) =>
            getFailedShareName(share, membersToAdd),
          ),
        );
      } catch {
        failed += membersToAdd.length;
        failedShareNames.push(
          ...membersToAdd.map((share) => share.name || String(share.id)),
        );
      }
    }

    if (membersToDelete.length > 0) {
      try {
        const result: DeleteSharesOutput = await new Promise(
          (resolve, reject) => {
            deleteAuthShares(
              { connectionId: id, shares: formatShares(membersToDelete) },
              { onSuccess: resolve, onError: reject },
            );
          },
        );
        successful += result.successful;
        failed += result.failed;
        failedShareNames.push(
          ...result.failedShares.map((share) =>
            getFailedShareName(share, membersToDelete),
          ),
        );
      } catch {
        failed += membersToDelete.length;
        failedShareNames.push(
          ...membersToDelete.map((share) => share.name || String(share.id)),
        );
      }
    }

    const hasTitle = connection?.title ? "true" : "false";
    const connectionTitle = connection?.title ?? "";

    if (failed > 0 && successful === 0) {
      createNotification({
        message: t("notifications.unable-to-share", {
          hasTitle,
          connectionTitle,
        }),
        type: "error",
      });
      return;
    }

    if (failed > 0) {
      createNotification({
        message: t("notifications.partially-shared", {
          hasTitle,
          connectionTitle,
          failedNames: failedShareNames.join(", "),
        }),
        type: "warning",
      });
      return;
    }

    createNotification({
      message: t("notifications.sharing-updated", {
        hasTitle,
        connectionTitle,
      }),
      type: "success",
    });
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return null;
  }

  return (
    <ConnectionShareContent
      connection={connection}
      id={id}
      isOpen={isOpen}
      onSubmit={handleSubmit}
      setIsOpen={setIsOpen}
      disableShare={!connection?.permissions?.share}
      isRemoveOnly={isShareBlocked}
    />
  );
}
